import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import useOptimizedFormWatch from './hooks/useOptimizedFormWatch';

function Pricing() {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const watchedValues = useOptimizedFormWatch();

  // Memoize pricing sections to prevent unnecessary re-renders
  const singleProductPricing = useMemo(() => {
    if (watchedValues.variantMode !== 'single') return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label
            htmlFor="original-price"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mr-2"></span>
            Original Price <span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                $
              </span>
            </div>
            <Input
              id="original-price"
              type="number"
              step="0.01"
              placeholder="0.00"
              className="h-10 pl-8 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200 text-sm"
              {...register('original_price', {
                required: 'Original Price is required',
              })}
            />
          </div>
          {errors?.original_price && (
            <p className="text-red-500 text-xs font-medium">
              {typeof errors?.original_price?.message === 'string' &&
                errors.original_price.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="sale-price"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mr-2"></span>
            Sale Price
          </Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                $
              </span>
            </div>
            <Input
              id="sale-price"
              type="number"
              step="0.01"
              placeholder="0.00"
              className="h-10 pl-8 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm"
              {...register('sale_price')}
            />
          </div>
          {typeof errors?.sale_price?.message === 'string' && (
            <p className="text-red-500 text-xs font-medium">
              {errors.sale_price.message}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Optional discounted price
          </p>
        </div>
      </div>
    );
  }, [watchedValues.variantMode, register, errors]);

  const variantProductPricing = useMemo(() => {
    if (watchedValues.variantMode !== 'variant') return null;

    return (
      <div className="space-y-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg
                className="w-3 h-3 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Variant Product Pricing
              </h4>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                This is a reference price. Each variant will have its own
                specific pricing. Individual variant prices will be set in the
                Variants section below.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="base-price"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mr-2"></span>
            Base Price (Reference Only)
          </Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                $
              </span>
            </div>
            <Input
              id="base-price"
              type="number"
              step="0.01"
              placeholder="0.00"
              className="h-10 pl-8 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all duration-200 text-sm"
              {...register('base_price')}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            Optional reference price - actual prices set per variant
          </p>
        </div>
      </div>
    );
  }, [watchedValues.variantMode, register]);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Pricing
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {watchedValues.variantMode === 'variant'
                ? 'Set base pricing - variants will have their own prices'
                : 'Set product pricing information'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Single Product Pricing */}
        {singleProductPricing}

        {/* Variant Product Pricing */}
        {variantProductPricing}

        {/* Stock Quantity - Always shown */}
        <div className="space-y-2">
          <Label
            htmlFor="stock-quantity"
            className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center"
          >
            <span className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mr-2"></span>
            {watchedValues.variantMode === 'variant'
              ? 'Base Stock Quantity'
              : 'Stock Quantity'}
          </Label>
          <Input
            id="stock-quantity"
            type="number"
            min="0"
            placeholder="Enter stock quantity"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 rounded-lg transition-all duration-200 text-sm"
            {...register('stock_quantity')}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
            {watchedValues.variantMode === 'variant'
              ? 'Base stock - individual variant stock managed in Variants section'
              : 'Leave empty for unlimited stock'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
